import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { mapboxgl, MAPBOX_STYLE, MAPBOX_TOKEN, BRAND_BLUE } from "@/lib/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import { Layout } from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { SealCheck, Phone, Globe, MapPin, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ProListing() {
  const { slug } = useParams();
  const [listing, setListing] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    zip: "", job_type: "car", message: "", preferred_contact: "email",
  });

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    api.get(`/finder/pro/${slug}`)
      .then((r) => setListing(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  // Record a view event when listing loads
  useEffect(() => {
    if (!listing?.id) return;
    api.post("/finder/events", { listing_id: listing.id, event_type: "view" }).catch(() => {});
  }, [listing?.id]);

  // Mount Mapbox mini-map
  useEffect(() => {
    if (!listing?.lat || !listing?.lng || !mapContainerRef.current || mapRef.current || !MAPBOX_TOKEN) return;
    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAPBOX_STYLE,
      center: [listing.lng, listing.lat],
      zoom: 10,
      attributionControl: false,
    });
    const el = document.createElement("div");
    el.style.cssText = `width:24px;height:24px;border-radius:50%;background:${BRAND_BLUE};border:3px solid #0A0A0A;box-shadow:0 0 0 2px ${BRAND_BLUE},0 4px 10px rgba(0,0,0,.5)`;
    new mapboxgl.Marker({ element: el }).setLngLat([listing.lng, listing.lat]).addTo(mapRef.current);
    mapRef.current.addControl(new mapboxgl.AttributionControl({ compact: true }), "bottom-right");
    return () => { mapRef.current?.remove(); mapRef.current = null; };
  }, [listing]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submitQuote = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/finder/leads", { listing_id: listing.id, ...form });
      setSent(true);
      toast.success("Request sent");
    } catch (err) { toast.error(formatApiError(err)); }
    setSending(false);
  };

  const trackPhone = () => api.post("/finder/events", { listing_id: listing.id, event_type: "phone_click" }).catch(() => {});
  const trackWebsite = () => api.post("/finder/events", { listing_id: listing.id, event_type: "website_click" }).catch(() => {});

  if (notFound) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 lg:px-8 py-24 text-center">
          <div className="section-label mb-3">— 404</div>
          <h1 className="font-chivo font-black text-4xl text-white uppercase tracking-tight">Profile not found.</h1>
          <p className="font-plex text-[#9CA3AF] mt-4">This listing may have been removed or is pending approval.</p>
          <Link to="/find" className="btn-primary mt-6 inline-flex" data-testid="pro-404-back"><ArrowLeft size={14} weight="bold" /> Back to map</Link>
        </div>
      </Layout>
    );
  }

  if (!listing) {
    return <Layout><div className="max-w-3xl mx-auto px-4 py-24 font-mono text-xs text-[#52525B] text-center">[LOADING PROFILE]</div></Layout>;
  }

  const hasCoords = Number.isFinite(listing.lat) && Number.isFinite(listing.lng);

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10">
        <Link to="/find" className="font-mono text-xs text-[#52525B] hover:text-[#00D4FF] inline-flex items-center gap-1 mb-6" data-testid="pro-back-to-map">
          <ArrowLeft size={12} weight="bold" /> BACK TO MAP
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 space-y-6">
            <div className="surface p-6 lg:p-8" data-testid="pro-header">
              <div className="flex items-center gap-2 mb-2">
                <div className="section-label">— VERIFIED PRO</div>
                {listing.verified && <SealCheck size={14} weight="fill" className="text-[#00D4FF]" />}
              </div>
              <h1 className="font-chivo font-black text-3xl sm:text-4xl text-white uppercase tracking-tighter" data-testid="pro-name">{listing.business_name}</h1>
              <div className="font-mono text-xs text-[#9CA3AF] mt-2 flex items-center gap-1">
                <MapPin size={12} weight="duotone" className="text-[#00D4FF]" /> {listing.city}, {listing.state} · Serves within {listing.service_radius_mi} mi
              </div>
              {listing.about && <p className="font-plex text-[#9CA3AF] leading-relaxed mt-5" data-testid="pro-about">{listing.about}</p>}

              <div className="flex flex-wrap gap-2 mt-5">
                {(listing.service_types || []).map((t) => <span key={t} className="tag tag-active" data-testid={`pro-type-${t}`}>{t.toUpperCase()}</span>)}
                {(listing.service_style || []).map((s) => <span key={s} className="tag">{s === "mobile" ? "MOBILE" : "IN-SHOP"}</span>)}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6 border-t border-[#262626] pt-5">
                {listing.phone && (
                  <a href={`tel:${listing.phone.replace(/[^0-9+]/g, "")}`} onClick={trackPhone} className="flex items-center gap-2 font-mono text-sm text-white hover:text-[#00D4FF]" data-testid="pro-phone">
                    <Phone size={16} weight="duotone" className="text-[#00D4FF]" /> {listing.phone}
                  </a>
                )}
                {listing.website && (
                  <a href={listing.website} target="_blank" rel="noopener noreferrer" onClick={trackWebsite} className="flex items-center gap-2 font-mono text-sm text-white hover:text-[#00D4FF] truncate" data-testid="pro-website">
                    <Globe size={16} weight="duotone" className="text-[#00D4FF]" /> {listing.website.replace(/^https?:\/\//, "")}
                  </a>
                )}
              </div>
            </div>

            {(listing.sub_services?.length > 0) && (
              <div className="surface p-6" data-testid="pro-services">
                <div className="section-label mb-3">— SERVICES</div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {listing.sub_services.map((s) => (
                    <li key={s} className="flex items-start gap-2 font-plex text-sm text-white">
                      <CheckCircle size={16} weight="fill" className="text-[#00D4FF] shrink-0 mt-0.5" /> {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {listing.photos?.length > 0 && (
              <div className="surface p-6" data-testid="pro-photos">
                <div className="section-label mb-3">— WORK PHOTOS</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {listing.photos.map((src, i) => (
                    <img key={i} src={src} alt="" className="w-full aspect-square object-cover border border-[#262626]" />
                  ))}
                </div>
              </div>
            )}

            {hasCoords && (
              <div className="h-[360px] border border-[#262626]" data-testid="pro-map">
                <div ref={mapContainerRef} style={{ width: "100%", height: "100%", background: "#0A0A0A" }} />
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="surface p-6 lg:p-8 lg:sticky lg:top-24" data-testid="pro-quote-card">
              <div className="section-label mb-2">— REQUEST A QUOTE</div>
              <h2 className="font-chivo font-black text-xl text-white uppercase tracking-tight mb-1">
                Contact {listing.business_name}
              </h2>
              <p className="font-plex text-xs text-[#9CA3AF] mb-5">No fee, no middleman. Your details go straight to this contractor.</p>

              {sent ? (
                <div className="text-center py-8">
                  <SealCheck size={42} weight="fill" className="text-[#00D4FF] mx-auto mb-3" />
                  <div className="font-chivo font-bold text-white uppercase tracking-tight">Request sent</div>
                  <p className="font-plex text-sm text-[#9CA3AF] mt-2">Expect a reply within one business day.</p>
                </div>
              ) : (
                <form onSubmit={submitQuote} className="space-y-3" data-testid="pro-quote-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input required placeholder="Your name" value={form.customer_name} onChange={onChange("customer_name")} className="input-rugged" data-testid="pro-quote-name" />
                    <input required inputMode="numeric" pattern="[0-9]*" maxLength={5} placeholder="Your zip" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value.replace(/\D/g, "") })} className="input-rugged" data-testid="pro-quote-zip" />
                  </div>
                  <input type="email" required placeholder="Email" value={form.customer_email} onChange={onChange("customer_email")} className="input-rugged" data-testid="pro-quote-email" />
                  <input placeholder="Phone (optional)" value={form.customer_phone} onChange={onChange("customer_phone")} className="input-rugged" data-testid="pro-quote-phone" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <select value={form.job_type} onChange={onChange("job_type")} className="input-rugged" data-testid="pro-quote-job-type">
                      <option value="car">Car / Detailing</option>
                      <option value="industrial">Industrial / Machine</option>
                      <option value="marine">Marine</option>
                      <option value="restoration">Fire / Smoke / Mold</option>
                      <option value="other">Other</option>
                    </select>
                    <select value={form.preferred_contact} onChange={onChange("preferred_contact")} className="input-rugged" data-testid="pro-quote-contact-pref">
                      <option value="email">Email me</option>
                      <option value="phone">Call me</option>
                      <option value="either">Either is fine</option>
                    </select>
                  </div>
                  <textarea required placeholder="Describe the job: surface, size, timing." rows={5} value={form.message} onChange={onChange("message")} className="input-rugged" data-testid="pro-quote-message" />
                  <button type="submit" disabled={sending} className="btn-primary w-full" data-testid="pro-quote-submit">
                    {sending ? "Sending..." : "Send Request"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
